import React, { useCallback, useEffect, useRef, useState } from 'react';
import DebouncedTextInput from './debounced-text-input';
import { PatternFlyValidated, VALIDATION_DEFAULT, VALIDATION_ERROR, VALIDATION_WARNING } from './validation-types';
import { useTranslation } from 'react-i18next';
import ValidationHelperText from './validation-helper-text';
import { Button, InputGroup, InputGroupItem } from '@patternfly/react-core';
import { AngleUpIcon, AngleDownIcon, EraserIcon } from '@patternfly/react-icons';

import './number-input.css';

/** Number of decimal places in a step value, used to round spinner results. */
const getStepPrecision = (step: number): number => {
    const str = step.toString();
    const dotIndex = str.indexOf('.');
    return dotIndex === -1 ? 0 : str.length - dotIndex - 1;
};

export enum ValidRangeDisplay {
    /** Always show the valid range hint (default). */
    Always = 'always',
    /** Show valid range hint only on hover or focus (via CSS). */
    OnInteraction = 'onInteraction',
    /** Never show the valid range hint (only show validation errors). */
    Hidden = 'hidden',
}

export interface NumberInputProps {
    fieldId: string,
    defaultValue: number | undefined,
    /** onChange function will only be called if the current number passes validation */
    onChange: (value: number) => void,
    /** Minimum allowed value for validation */
    lowerBound?: number,
    /** Maximum allowed value for validation */
    upperBound?: number,
    /** Optional unit to display in validation and valid range messages */
    unit?: string,
    /** Controls when the valid range hint is displayed. Defaults to Always. */
    validRangeDisplay?: ValidRangeDisplay,
    /** If set to 'warning' or 'error', overrides the visual validation state. Does not affect internal validation logic. */
    validationOverride?: PatternFlyValidated,
    /** When true, an empty input is considered valid. Use onEmpty to handle the empty state. */
    allowEmpty?: boolean,
    /** Called when the input is cleared (only meaningful when allowEmpty is true) */
    onEmpty?: () => void,
    /** Show up/down spinner arrow buttons for stepping values */
    showSpinnerButtons?: boolean,
    /** Step value for spinner buttons (default 1) */
    step?: number,
    /** Show a clear button that sets the field to empty (only meaningful when allowEmpty is true) */
    showClearButton?: boolean,
    /** Optional `data-testid` forwarded to the underlying text input — useful for e2e tests
     *  that need a stable selector independent of the dynamically-built `fieldId`. */
    testId?: string,
}

enum NumberValidationState {
    Ok = 'Ok',
    InvalidNumber = 'InvalidNumber',
    OutOfBounds = 'OutOfBounds',
}

const getInputValidationState = (numberValidationState: NumberValidationState) => {
    switch (numberValidationState) {
        case NumberValidationState.Ok:
            return VALIDATION_DEFAULT;
        case NumberValidationState.InvalidNumber:
            return VALIDATION_ERROR;
        case NumberValidationState.OutOfBounds:
            return VALIDATION_WARNING;
        default:
            throw new Error(`Invalid validation state: ${numberValidationState}`);
    }
}

const validateNumber = (value: string, lowerBound?: number, upperBound?: number, allowEmpty?: boolean): { validationResult: NumberValidationState, valueAsNumber: number } => {
    if (allowEmpty && value.trim() === '') {
        return { validationResult: NumberValidationState.Ok, valueAsNumber: NaN };
    }

    const valueAsNumber = parseFloat(value);

    // 1. Check if number is valid
    let validationResult = !isNaN(valueAsNumber) ? NumberValidationState.Ok : NumberValidationState.InvalidNumber;

    // 2. Check if number is out of bounds
    if (validationResult === NumberValidationState.Ok && lowerBound !== undefined) { if (valueAsNumber < lowerBound) { validationResult = NumberValidationState.OutOfBounds; } }
    if (validationResult === NumberValidationState.Ok && upperBound !== undefined) { if (valueAsNumber > upperBound) { validationResult = NumberValidationState.OutOfBounds; } }

    return { validationResult, valueAsNumber };
}

/**
 * NumberInput is a UI component that allows users to enter strings which are parsed as numbers.
 * @param props
 * @returns
 */
const NumberInput = (props: NumberInputProps) => {
    const { fieldId, defaultValue, onChange, lowerBound, upperBound, validRangeDisplay = ValidRangeDisplay.Always, validationOverride,
        unit, allowEmpty, onEmpty, showSpinnerButtons, step = 1, showClearButton, testId } = props;
    const showValidRange = validRangeDisplay !== ValidRangeDisplay.Hidden;
    const showOnInteraction = validRangeDisplay === ValidRangeDisplay.OnInteraction;

    const { t } = useTranslation();

    const [validationState, setValidationState] = useState<NumberValidationState>(NumberValidationState.Ok);
    const [internalValue, setInternalValue] = useState('');

    const inputRef = useRef<HTMLInputElement | null>(null);

    // perform internal setup on initialization & when defaultValue changes
    useEffect(() => {
        const stringValue = defaultValue !== undefined ? defaultValue.toString() : '';
        setInternalValue(stringValue);
        // Validate immediately so out-of-bounds defaults show warnings on mount
        const { validationResult } = validateNumber(stringValue, lowerBound, upperBound, allowEmpty);
        setValidationState(validationResult);
    }, [defaultValue, lowerBound, upperBound, allowEmpty]);

    // perform validation for the typed-in number both immediately and on a debounce
    const handleChange = useCallback((value: string) => {
        setInternalValue(value);
        const { validationResult, valueAsNumber } = validateNumber(value, lowerBound, upperBound, allowEmpty);

        setValidationState(validationResult);

        if (validationResult === NumberValidationState.Ok) {
            if (isNaN(valueAsNumber)) {
                // empty value (allowEmpty case)
                onEmpty?.();
            } else {
                onChange(valueAsNumber);

                // update internal value with the internal value converted into a regular number
                // (which in practice converts the value to a "regular" number e.g. "1." -> 1, ".1" -> 0.1, "1e2" -> 100)
                // IF the input value was valid AND this component is no longer focused
                // (we don't want to set the value of the internal value while user is editing it)
                if (!inputRef.current || !document.activeElement || document.activeElement !== inputRef.current) {
                    setInternalValue(valueAsNumber.toString());
                }
            }
        }

        return validationResult;
    }, [lowerBound, upperBound, allowEmpty, onChange, onEmpty]);

    const handleBlur = useCallback((value: string) => {
        // don't do anything if user never changed the value (or reverted it back to the original one), otherwise...
        if (value !== (defaultValue?.toString() || '')) {
            // revert form back to previous valid value on blur if validation is currently not ok
            const result = handleChange(value);
            if (result !== NumberValidationState.Ok) {
                if (allowEmpty && defaultValue === undefined) {
                    handleChange('');
                } else {
                    handleChange(defaultValue?.toString() || '');
                }
            }
        }
    }, [defaultValue, allowEmpty, handleChange]);

    const handleStepUp = useCallback(() => {
        const current = parseFloat(internalValue);
        const base = isNaN(current) ? (lowerBound ?? 0) : current;
        const precision = getStepPrecision(step);
        const raw = upperBound !== undefined ? Math.min(base + step, upperBound) : base + step;
        const newValue = parseFloat(raw.toFixed(precision));
        handleChange(newValue.toString());
    }, [internalValue, lowerBound, upperBound, step, handleChange]);

    const handleStepDown = useCallback(() => {
        const current = parseFloat(internalValue);
        const base = isNaN(current) ? (lowerBound ?? 0) : current;
        const precision = getStepPrecision(step);
        const raw = lowerBound !== undefined ? Math.max(base - step, lowerBound) : base - step;
        const newValue = parseFloat(raw.toFixed(precision));
        handleChange(newValue.toString());
    }, [internalValue, lowerBound, step, handleChange]);

    const handleClear = useCallback(() => {
        handleChange('');
    }, [handleChange]);

    const currentNumber = parseFloat(internalValue);
    const isAtLowerBound = !isNaN(currentNumber) && lowerBound !== undefined && currentNumber <= lowerBound;
    const isAtUpperBound = !isNaN(currentNumber) && upperBound !== undefined && currentNumber >= upperBound;

    const getValidationErrorMessage = useCallback((numberValidationState: NumberValidationState, isValidRangeShown: boolean) => {

        let lower = lowerBound !== undefined ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, }).format(lowerBound) : lowerBound;
        let upper = upperBound !== undefined ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, }).format(upperBound) : upperBound;

        // add unit strings to bound values
        if (unit !== undefined) {
            lower = `${lower} ${unit}`;
            upper = `${upper} ${unit}`;
        }

        switch (numberValidationState) {
            case NumberValidationState.Ok:
                if (!isValidRangeShown) {
                    return undefined;
                } else {
                    if (lowerBound !== undefined && upperBound !== undefined) {
                        return t('component.numberInput.validRange.both', { lowerBound: lower, upperBound: upper });
                    }
                    else if (lowerBound === undefined && upperBound !== undefined) {
                        return t('component.numberInput.validRange.upperBound', { upperBound: upper });
                    } else if (lowerBound !== undefined && upperBound === undefined) {
                        return t('component.numberInput.validRange.lowerBound', { lowerBound: lower });
                    } else {
                        return undefined;
                    }
                }
            case NumberValidationState.InvalidNumber:
                return t('component.numberInput.validation.enterValidNumber');
            case NumberValidationState.OutOfBounds:
                if (lowerBound !== undefined && upperBound !== undefined) {
                    return t('component.numberInput.validation.outOfBounds.both', { lowerBound: lower, upperBound: upper });
                }
                else if (lowerBound === undefined && upperBound !== undefined) {
                    return t('component.numberInput.validation.outOfBounds.upperBound', { upperBound: upper });
                } else if (lowerBound !== undefined && upperBound === undefined) {
                    return t('component.numberInput.validation.outOfBounds.lowerBound', { lowerBound: lower });
                }
                else {
                    throw new Error('No bounds were defined!');
                }
            default:
                throw new Error(`Invalid validation state: ${numberValidationState}`);
        }
    }, [unit, lowerBound, upperBound, t]);

    const effectiveValidation = (validationOverride === VALIDATION_ERROR || validationOverride === VALIDATION_WARNING) ? validationOverride
        : getInputValidationState(validationState);

    const textInput = (
        <DebouncedTextInput
            fieldId={fieldId}
            defaultValue={internalValue}
            onDebouncedChange={handleChange}
            onImmediateChange={handleChange}
            onBlur={handleBlur}
            type="text"
            validated={effectiveValidation}
            debounceTimeMs={150}
            inputRef={inputRef}
            testId={testId}
        />
    );

    const hasAddonButtons = showSpinnerButtons || (showClearButton && allowEmpty);

    return (
        <div className={showOnInteraction ? 'number-input-interactive-range' : undefined}>
            {hasAddonButtons ? (
                <InputGroup>
                    <InputGroupItem isFill>{textInput}</InputGroupItem>
                    {showSpinnerButtons && (
                        <InputGroupItem>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Button
                                    variant="control"
                                    isDisabled={isAtUpperBound}
                                    onClick={handleStepUp}
                                    aria-label={t('component.numberInput.spinnerUp')}
                                    className="number-input-spinner-up"
                                >
                                    <AngleUpIcon />
                                </Button>
                                <Button
                                    variant="control"
                                    isDisabled={isAtLowerBound}
                                    onClick={handleStepDown}
                                    aria-label={t('component.numberInput.spinnerDown')}
                                    className="number-input-spinner-down"
                                >
                                    <AngleDownIcon />
                                </Button>
                            </div>
                        </InputGroupItem>
                    )}
                    {showClearButton && allowEmpty && (
                        <InputGroupItem>
                            <Button
                                variant="control"
                                onClick={handleClear}
                                isDisabled={internalValue.trim() === ''}
                                aria-label={t('component.numberInput.clear')}
                            >
                                <EraserIcon />
                            </Button>
                        </InputGroupItem>
                    )}
                </InputGroup>
            ) : (
                textInput
            )}
            <ValidationHelperText
                validated={effectiveValidation}
                helperText={getValidationErrorMessage(validationState, showValidRange)}
            />
        </div>
    );
}

export default NumberInput;
